import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useRoleBase } from "../../hooks/useRoleBase";
import { PageHeader, StatusPill, EmptyState, Spinner } from "../../components/dashboard/DashboardShell";
import { fmtDateTime, timeAgo } from "../../lib/format";

const avatar = (p) => p?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(p?.name || "U")}&background=2563eb&color=fff`;

function ChatList({ chats, activeId, onSelect }) {
  if (!chats?.length) return <EmptyState icon="chat" title="Belum ada percakapan" desc="Chat tersedia setelah pesanan dibayar." />;
  return (
    <div className="divide-y divide-slate-50" data-testid="chat-list">
      {chats.map((c) => (
        <button key={c.order_id} onClick={() => onSelect(c.order_id)} data-testid={`chat-item-${c.order_id}`}
          className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-slate-50 transition-colors ${activeId === c.order_id ? "bg-blue-50/60" : ""}`}>
          <img src={avatar(c.other)} alt="" className="w-11 h-11 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[13px] font-bold text-slate-900 truncate">{c.other?.name}</p>
              <span className="text-[10px] text-slate-400 whitespace-nowrap">{c.last_message ? timeAgo(c.last_message.created_at) : ""}</span>
            </div>
            <p className="text-[11px] text-slate-500 truncate">{c.vehicle_type_label} · {c.order_id}</p>
            <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message?.text || "Mulai percakapan"}</p>
          </div>
          {c.unread > 0 && <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.unread}</span>}
        </button>
      ))}
    </div>
  );
}

function ChatWindow({ orderId, chat, onBack }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const { data: msgs, isLoading } = useQuery({ queryKey: ["messages", orderId], queryFn: () => api.get(`/orders/${orderId}/messages`).then((r) => r.data), refetchInterval: 4000 });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/orders/${orderId}/messages`, { text: text.trim() }); setText(""); qc.invalidateQueries({ queryKey: ["messages", orderId] }); qc.invalidateQueries({ queryKey: ["chats"] }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]" data-testid="chat-window">
      <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
        <button onClick={onBack} className="lg:hidden p-1 text-slate-500"><ArrowLeft size={18} /></button>
        <img src={avatar(chat?.other)} alt="" className="w-10 h-10 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate" data-testid="chat-other-name">{chat?.other?.name}</p>
          <p className="text-[11px] text-slate-400">{chat?.vehicle_type_label} · {orderId}</p>
        </div>
        {chat && <StatusPill status={chat.status} />}
      </div>
      <div className="flex-1 overflow-y-auto py-4 space-y-3 px-1" data-testid="chat-messages">
        {isLoading ? <Spinner /> : !msgs?.length ? <p className="text-center text-xs text-slate-400 py-10">Belum ada pesan. Sapa lawan bicara Anda!</p> : msgs.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${mine ? "bg-blue-600 text-white rounded-br-md" : "bg-white border border-slate-100 text-slate-800 rounded-bl-md"}`}>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-blue-100" : "text-slate-400"}`}>{fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 pt-3 border-t border-slate-100">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis pesan..." data-testid="chat-input"
          className="flex-1 h-11 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send-btn" className="h-11 w-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center disabled:opacity-50">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </form>
    </div>
  );
}

export default function ChatPage() {
  const { orderId } = useParams();
  const base = useRoleBase();
  const navigate = useNavigate();
  const { data: chats, isLoading } = useQuery({ queryKey: ["chats"], queryFn: () => api.get("/chats").then((r) => r.data), refetchInterval: 10000 });
  const active = chats?.find((c) => c.order_id === orderId);
  return (
    <div>
      <PageHeader title="Chat" subtitle="Komunikasi langsung terkait pesanan Anda." />
      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-2 overflow-y-auto max-h-[calc(100vh-200px)] ${orderId ? "hidden lg:block" : ""}`}>
          {isLoading ? <Spinner /> : <ChatList chats={chats} activeId={orderId} onSelect={(id) => navigate(`${base}/chat/${id}`)} />}
        </div>
        <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-4 ${!orderId ? "hidden lg:flex items-center justify-center" : ""}`}>
          {orderId ? <ChatWindow orderId={orderId} chat={active} onBack={() => navigate(`${base}/chat`)} /> : <p className="text-sm text-slate-400">Pilih percakapan di sebelah kiri.</p>}
        </div>
      </div>
    </div>
  );
}
